import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RECEIPT_FORMAT, type Payment, type ReceiptFormat } from "../services/paymentsApi";

interface ReprintPaymentReceiptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  payment: Payment | null;
  isSubmitting?: boolean;
  onSubmit: (payload: { reprintReason: string; format: ReceiptFormat }) => void;
}

export const ReprintPaymentReceiptDialog = ({
  open,
  onOpenChange,
  payment,
  isSubmitting = false,
  onSubmit,
}: ReprintPaymentReceiptDialogProps) => {
  const [reprintReason, setReprintReason] = useState("");
  const [format, setFormat] = useState<ReceiptFormat>(RECEIPT_FORMAT.Thermal80);

  useEffect(() => {
    if (!open) {
      return;
    }

    setReprintReason("");
    setFormat(RECEIPT_FORMAT.Thermal80);
  }, [open, payment?.id]);

  const handleSubmit = () => {
    const reason = reprintReason.trim();
    if (!reason) {
      return;
    }

    onSubmit({
      reprintReason: reason,
      format,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Reprint Receipt</DialogTitle>
          <DialogDescription>
            {payment
              ? `Provide a reason to reprint receipt for ${payment.paymentNumber}.`
              : "Provide a reason to reprint this receipt."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Reprint Reason</Label>
            <Textarea
              value={reprintReason}
              onChange={(event) => setReprintReason(event.target.value)}
              placeholder="Why are you reprinting this receipt?"
              disabled={isSubmitting}
            />
          </div>

          <div className="space-y-2">
            <Label>Receipt Format</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={format === RECEIPT_FORMAT.Thermal80 ? "default" : "outline"}
                size="sm"
                onClick={() => setFormat(RECEIPT_FORMAT.Thermal80)}
                disabled={isSubmitting}
              >
                Thermal (80mm)
              </Button>
              <Button
                type="button"
                variant={format === RECEIPT_FORMAT.A4 ? "default" : "outline"}
                size="sm"
                onClick={() => setFormat(RECEIPT_FORMAT.A4)}
                disabled={isSubmitting}
              >
                A4
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !reprintReason.trim()}>
            {isSubmitting ? "Reprinting..." : "Reprint Receipt"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
